<script lang="ts">
	import { onMount } from 'svelte';
	import { getAuthContext } from '../../components/NewsHeader/AuthDialogs/utils/getAuthContext/getAuthContext';
	import { NewsPageBody } from '../../components/NewsPageBody';
	import { NewsRoutes } from '../../constants/routes';
	import { getNewsletterSectionUrl } from './utils/getNewsletterSectionUrl/getNewsletterSectionUrl';
	import { Config } from '#lib/constants/config.js';
	import { trackOnwardClicks } from '#lib/features/analytics/utils/trackOnwardClicks/trackOnwardClicks.js';
	import { ArticleReadTracker } from '#lib/features/news/components/ArticleReadTracker/index.js';
	import { NewsMixpanelEvents } from '#lib/features/news/constants/mixpanel-events.js';
	import { useScrollDepthTracking } from '#lib/features/news/runes/useScrollDepthTracking/useScrollDepthTracking.svelte.js';
	import {
		ArticleContentType,
		getArticleMetadata,
	} from '#lib/features/news/utils/getArticleMetadata/getArticleMetadata.js';
	import { trackArticleRead } from '#lib/features/news/utils/trackArticleRead/trackArticleRead.js';
	import { SubscribeToNewsletterDocument } from '#lib/features/newsletter/mutations/__generated__/subscribeToNewsletter.generated.js';
	import { gqlClientMutations } from '#lib/gqlClientMutations.js';
	import { page } from '$app/state';
	import { Container } from '$global/components/Container';
	import { showToast } from '$global/components/Toasts';
	import { getPageData } from '$global/utils/getPageData/getPageData';
	import { replaceShallowState } from '$global/utils/replaceShallowState/replaceShallowState';
	import type { NewsNewsletterPDPPageData } from '$routes/news/newsletter/[slug]/types';

	const { user } = getAuthContext();

	let data = $derived(getPageData<NewsNewsletterPDPPageData>(page.data));
	let articleHtml = $derived(data.articleHtml);

	// A newsletter issue carries no artist or topic tags, so the content type and
	// publish date are all the shared model can populate here.
	let newsletterMetadata = $derived(
		getArticleMetadata({
			contentType: ArticleContentType.Newsletter,
			publishDate: data.newsletter?.date_published,
		})
	);

	// The newsletter body is a raw CMS html blob, so its links are plain anchors
	// with no component to hang a handler on — one delegated listener over the
	// blob is the only way to catch the crossings into Editions, Artists and the
	// Shop that the issue links out to (HB-967).
	const onwardClickParams = {
		eventName: NewsMixpanelEvents.OnwardClick,
		sourceElement: 'newsletter-body',
		fromSection: 'news' as const,
	};

	// The guard keeps the read event to one per newsletter: the effect also
	// reruns when page.data recomputes without changing newsletters.
	let trackedNewsletterId: string | undefined;

	$effect(() => {
		if (data.newsletter?.id && data.newsletter.id !== trackedNewsletterId) {
			trackedNewsletterId = data.newsletter.id;

			trackArticleRead({
				articleId: data.newsletter.id,
				section: 'newsletter',
				metadata: newsletterMetadata,
			});
		}
	});

	// Separate from the read effect above: this one has to re-run (and tear the
	// old observer down) whenever the newsletter changes, so it reads the id
	// reactively instead of guarding on it. The in-app surface scrolls the
	// overlay element rather than the window, same as the anchor handling below.
	$effect(() =>
		useScrollDepthTracking({
			articleId: data.newsletter?.id,
			section: 'newsletter',
			target: data.showGlobalNav
				? window
				: (document.getElementById('news-ios-overlay') ?? undefined),
		})
	);

	// Moves a story's anchor from the fragment to `?section=`, so the address
	// the reader copies previews as that story: the fragment never reaches the
	// server, which is where the preview's meta tags are set.
	const syncSectionUrl = () => {
		const anchor = window.location.hash.slice(1);

		if (!document.getElementById(anchor)?.classList.contains('anchors')) {
			return;
		}

		replaceShallowState(
			getNewsletterSectionUrl(window.location.href, anchor),
			page.state
		);
	};

	onMount(() => {
		if (!data.showGlobalNav) {
			const shareSpanElt = document.getElementById('news-newsletter-share');

			if (shareSpanElt) {
				const text = `HENI News: ${data.newsletter.subject}`;
				const url = `${Config.Domain}${NewsRoutes.Newsletter}/${data.newsletter.slug}`;

				shareSpanElt.innerHTML = `
					<a
						href=${`/news?shareApp=1&text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`}
						style="color: #C6412E; text-decoration: underline;">Share HENI Art News</a
					>
				`;
			}

			const signupButton = document.getElementById('news-newsletter-signup');

			if (signupButton) {
				signupButton.style.width = '118px';
				signupButton.style.height = '45px';
				signupButton.removeAttribute('href');
				signupButton.removeAttribute('target');
				signupButton.removeAttribute('rel');
				(signupButton as unknown as { onclick: () => void }).onclick =
					async () => {
						try {
							const initialInnerHtml = signupButton.innerHTML;

							signupButton.innerHTML = `
								<svg xmlns="http://www.w3.org/2000/svg" style="margin-top: 2px" viewBox="0 0 27 29" width="24" height="24" class="animate-spin absolute left-0 right-0 m-auto" data-cy="test-button-circular-progress"><!----><path d="M10.0464 22.167C8.27176 21.3536 6.82865 19.9577 5.95662 18.2111C5.08459 16.4645 4.83609 14.4722 5.25237 12.565C5.66865 10.6577 6.72467 
    8.9501 8.24514 7.72569C9.76562 6.50128 11.6591 5.83366 13.6113 5.83366C15.5635 5.83366 17.457 6.50127 18.9774 7.72569C20.4979 8.9501 21.5539 
    10.6577 21.9702 12.565C22.3865 14.4722 22.138 16.4645 21.266 18.2111C20.3939 19.9577 18.9508 21.3536 17.1762 22.167" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" style="fill-opacity:0" stroke="#909090"></path><!----></svg>
							`;
							signupButton.style.pointerEvents = 'none';

							await gqlClientMutations.request(SubscribeToNewsletterDocument, {
								email: $user?.attributes.email as string,
								listId: Config.NewsletterListIds.News,
							});

							signupButton.innerHTML = initialInnerHtml;
							const parentCell = signupButton.parentElement;
							if (parentCell) {
								parentCell.style.filter = 'brightness(0.5)';
							}

							showToast({
								variant: 'success',
								message: 'You subscribed to HENI Art News',
							});
						} catch {
							showToast({
								variant: 'success',
								message: 'Something went wrong. Please try again later.',
							});

							signupButton.style.pointerEvents = 'auto';
						}
					};
			}
		}

		setTimeout(() => {
			// A story is reached either by its anchor or by `?section=`, the form
			// that also gives the link its own preview.
			const anchor = window.location.hash.slice(1) || data.sectionId;

			if (!anchor) {
				return;
			}

			const node = document.getElementById(anchor);

			if (!node) {
				return;
			}

			syncSectionUrl();

			const y = node?.getBoundingClientRect?.()?.top + window.scrollY;

			const elt = data.showGlobalNav
				? window
				: document.getElementById('news-ios-overlay');

			if (elt) {
				elt.scroll({
					top: y,
					behavior: 'smooth',
				});
			}
		}, 100);
	});
</script>

<svelte:window onhashchange={syncSectionUrl} />

<NewsPageBody class="bg-gray-0">
	<Container dataCy="news-newsletter">
		<ArticleReadTracker
			articleId={data.newsletter?.id}
			section="newsletter"
			completionThreshold={750}
		>
			<div
				class="newsletter-article mx-auto mb-16 max-w-[720px] border border-gray-200"
				use:trackOnwardClicks={onwardClickParams}
			>
				<!-- eslint-disable-next-line svelte/no-at-html-tags -->
				{@html articleHtml}
			</div>
		</ArticleReadTracker>
	</Container>
</NewsPageBody>

<style>
	.newsletter-article {
		margin-top: 16px;
	}

	:global(p) {
		margin: 0 !important;
	}

	:global(.newsletter-article p) {
		margin: 13px 0 !important;
	}

	:global(.newsletter-article .story_divider > p) {
		margin-top: 0 !important;
	}

	:global(.anchors) {
		transform: translateY(-4rem);

		@media only screen and (min-width: 1024px) {
			transform: translateY(-10.5rem);
		}
	}

	/* Website specific code */
	@media (prefers-color-scheme: dark) {
		:global(
			.newsletter-article .white_text,
			.newsletter-article .white_text > div,
			.newsletter-article .white_text > div > a
		) {
			color: #000000 !important;
			mix-blend-mode: revert !important;
		}

		:global(.newsletter-article .dark_img img) {
			filter: invert(1);
		}
	}
</style>
